import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { SendEmailModal } from './SendEmailModal';
import { STATUS_EMAIL_MAP } from '../../utils/proManagerEmailGenerator';
import { 
  Users, TrendingUp, Calendar, DollarSign, Phone, Mail, MapPin, 
  Clock, CheckCircle, XCircle, AlertCircle, Search, Plus, Filter,
  ChevronRight, MoreVertical, Edit, Trash2, Eye, Send, FileText,
  Home, BarChart3, Settings, MessageSquare, CreditCard, Truck,
  Star, RefreshCw, ArrowUpRight, ArrowDownRight, Building2, Bell, LogOut
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const BRANCH_LIST = [
  { value: 'all', label: 'All Branches' },
  { value: 'berkshire', label: 'Berkshire' },
  { value: 'buckinghamshire', label: 'Buckinghamshire' },
  { value: 'hampshire', label: 'Hampshire' },
  { value: 'oxfordshire', label: 'Oxfordshire' },
  { value: 'surrey', label: 'Surrey' },
  { value: 'london', label: 'London' },
  { value: 'head_office', label: 'Head Office' },
];

const BRANCH_LABELS = {
  berkshire: 'Berkshire', buckinghamshire: 'Buckinghamshire', hampshire: 'Hampshire',
  oxfordshire: 'Oxfordshire', surrey: 'Surrey', london: 'London', head_office: 'Head Office',
};

const formatAppointment = (c) => {
  if (!c.appointment_date) return null;
  try {
    const d = new Date(c.appointment_date);
    const dateStr = d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
    return c.appointment_time ? `${dateStr}, ${c.appointment_time}` : dateStr;
  } catch {
    return c.appointment_time ? `${c.appointment_date}, ${c.appointment_time}` : c.appointment_date;
  }
};

// Customer Status Configuration
const CUSTOMER_STATUSES = {
  new_enquiry: { label: 'New Enquiry', color: 'bg-blue-500', icon: Users },
  quote_sent: { label: 'Quote Sent', color: 'bg-purple-500', icon: FileText },
  reminder_callback: { label: 'Reminder / Callback', color: 'bg-amber-500', icon: Bell },
  property_view: { label: 'Property View', color: 'bg-teal-500', icon: Home },
  pending_decision: { label: 'Pending Decision', color: 'bg-yellow-500', icon: Clock },
  deposit_requested: { label: 'Deposit Requested', color: 'bg-orange-500', icon: CreditCard },
  deposit_paid: { label: 'Deposit Paid', color: 'bg-emerald-500', icon: CheckCircle },
  job_booked: { label: 'Job Booked', color: 'bg-green-500', icon: Calendar },
  in_progress: { label: 'In Progress', color: 'bg-cyan-500', icon: Truck },
  completed: { label: 'Completed', color: 'bg-green-600', icon: CheckCircle },
  review_sent: { label: 'Review Sent', color: 'bg-indigo-500', icon: Star },
  declined: { label: 'Declined / Lost', color: 'bg-red-500', icon: XCircle }
};

// Email log type labels
const EMAIL_LOG_LABELS = {
  follow_up: 'Follow Up (Quote Chase)',
  deposit_request: 'Deposit Request',
  booking_confirmed: 'Booking Confirmed',
  review_request: 'Review Request',
  quotation: 'Quotation',
  confirmation: 'Confirmation',
  deposit: 'Deposit Received',
  review: 'Review Request',
  jobcomplete: 'Job Complete',
  whychoose: 'Why Choose Us',
  affiliate: 'Referral',
  partnership: 'Partnership',
  message: 'Custom Message',
  viewing: 'Property Viewing',
  callback: 'Callback',
};

// Lead Sources
const LEAD_SOURCES = [
  'Getamover', 'Google', 'Compare My Move', 'Referral Program', 
  'Nextdoor', 'Facebook', 'Instagram', 'Repeat Customer', 'Checkatrade',
  'Bark', 'Yell', 'Word of Mouth', 'Other'
];

// Package Options
const PACKAGES = [
  { id: 'van_man', name: 'Van & Man', price: 285 },
  { id: 'basic', name: 'Basic Move', price: 385 },
  { id: 'comfort', name: 'Comfort Move', price: 485 },
  { id: 'complete_comfort', name: 'Complete Comfort', price: 735 },
  { id: 'premium', name: 'Premium Move', price: 985 }
];

// Helper to get package info (handles both ID and name formats)
const getPackageInfo = (packageSelected) => {
  if (!packageSelected) return null;
  // Try to find by ID first
  let pkg = PACKAGES.find(p => p.id === packageSelected);
  // If not found, try to find by name (case-insensitive)
  if (!pkg) {
    pkg = PACKAGES.find(p => p.name.toLowerCase() === packageSelected.toLowerCase());
  }
  // If still not found, try partial match
  if (!pkg) {
    pkg = PACKAGES.find(p => packageSelected.toLowerCase().includes(p.name.toLowerCase().split(' ')[0]));
  }
  return pkg;
};

// Get display name for package
const getPackageName = (packageSelected) => {
  const pkg = getPackageInfo(packageSelected);
  return pkg ? pkg.name : packageSelected || 'Unknown';
};

// Stat Card Component
const StatCard = ({ title, value, icon: Icon, trend, trendUp, color = "gold" }) => (
  <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 hover:border-amber-500/30 transition-all">
    <div className="flex items-center justify-between mb-3">
      <div className={`p-2 rounded-lg ${color === 'gold' ? 'bg-amber-500/10' : 'bg-zinc-800'}`}>
        <Icon className={`w-5 h-5 ${color === 'gold' ? 'text-amber-500' : 'text-zinc-400'}`} />
      </div>
      {trend && (
        <div className={`flex items-center text-xs ${trendUp ? 'text-emerald-500' : 'text-red-500'}`}>
          {trendUp ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
          {trend}
        </div>
      )}
    </div>
    <p className="text-2xl font-bold text-white">{value}</p>
    <p className="text-xs text-zinc-500 mt-1">{title}</p>
  </div>
);

// Customer Row Component
const CustomerRow = ({ customer, onView, onEdit, onStatusChange, onDelete }) => {
  const [showActions, setShowActions] = useState(false);
  const status = CUSTOMER_STATUSES[customer.status] || CUSTOMER_STATUSES.new_enquiry;
  const StatusIcon = status.icon;

  return (
    <div 
      className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 hover:border-amber-500/30 transition-all cursor-pointer"
      onClick={() => onView(customer)}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <h3 className="font-semibold text-white truncate">{customer.name}</h3>
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium text-white ${status.color}`}>
              {status.label}
            </span>
            {customer.branch && (
              <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-zinc-700 text-amber-400" data-testid="customer-branch-badge">
                {BRANCH_LABELS[customer.branch] || customer.branch}
              </span>
            )}
            {customer.email_log?.length > 0 && (
              <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-blue-500/15 text-blue-400 border border-blue-500/30 flex items-center gap-1" data-testid="customer-email-count-badge" title={`${customer.email_log.length} email(s) sent`}>
                <Mail className="w-2.5 h-2.5" /> {customer.email_log.length}
              </span>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2 text-xs text-zinc-400">
            <div className="flex items-center gap-1">
              <Phone className="w-3 h-3" />
              <span className="truncate">{customer.phone || 'No phone'}</span>
            </div>
            <div className="flex items-center gap-1">
              <Mail className="w-3 h-3" />
              <span className="truncate">{customer.email || 'No email'}</span>
            </div>
            <div className="flex items-center gap-1">
              <MapPin className="w-3 h-3" />
              <span className="truncate">{customer.pickup_postcode || '?'} → {customer.delivery_postcode || '?'}</span>
            </div>
            <div className="flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              <span>{customer.move_date ? new Date(customer.move_date).toLocaleDateString('en-GB') : 'No date'}</span>
            </div>
          </div>
          {/* Show Quote Amount and Package */}
          <div className="mt-2 pt-2 border-t border-zinc-800 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-amber-500 font-bold">£{customer.quote_amount || 0}</span>
              {customer.package_selected && (
                <span className="text-[10px] bg-zinc-800 px-2 py-0.5 rounded text-zinc-400">
                  {getPackageName(customer.package_selected)}
                </span>
              )}
            </div>
            {customer.deposit_paid > 0 && (
              <span className="text-emerald-500 text-xs font-medium">
                Deposit: £{customer.deposit_paid}
              </span>
            )}
          </div>
        </div>
        <div className="relative">
          <button 
            onClick={(e) => { e.stopPropagation(); setShowActions(!showActions); }}
            className="p-2 hover:bg-zinc-800 rounded-lg transition-colors"
          >
            <MoreVertical className="w-4 h-4 text-zinc-500" />
          </button>
          {showActions && (
            <div className="absolute right-0 top-10 bg-zinc-800 border border-zinc-700 rounded-lg shadow-xl z-10 min-w-[140px]">
              <button onClick={(e) => { e.stopPropagation(); onView(customer); setShowActions(false); }} className="w-full px-3 py-2 text-left text-sm text-zinc-300 hover:bg-zinc-700 flex items-center gap-2">
                <Eye className="w-4 h-4" /> View
              </button>
              <button onClick={(e) => { e.stopPropagation(); onEdit(customer); setShowActions(false); }} className="w-full px-3 py-2 text-left text-sm text-zinc-300 hover:bg-zinc-700 flex items-center gap-2">
                <Edit className="w-4 h-4" /> Edit
              </button>
              <button onClick={(e) => { e.stopPropagation(); onDelete(customer); setShowActions(false); }} className="w-full px-3 py-2 text-left text-sm text-red-400 hover:bg-zinc-700 flex items-center gap-2">
                <Trash2 className="w-4 h-4" /> Delete
              </button>
            </div>
          )}
        </div>
      </div>
      {customer.lead_source && (
        <div className="mt-3 pt-3 border-t border-zinc-800">
          <span className="text-[10px] text-zinc-500 bg-zinc-800 px-2 py-1 rounded">
            Source: {customer.lead_source}
          </span>
        </div>
      )}
    </div>
  );
};

// Add/Edit Customer Modal
const CustomerModal = ({ customer, isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    pickup_address: '',
    delivery_address: '',
    pickup_postcode: '',
    delivery_postcode: '',
    move_date: '',
    move_time: '',
    notes: '',
    lead_source: '',
    quote_amount: '',
    deposit_paid: '',
    remaining_balance: '',
    package_selected: '',
    status: 'new_enquiry',
    job_notes: '',
    costs: { fuel: 0, labour: 0, parking: 0, tolls: 0, materials: 0, other: 0 }
  });

  useEffect(() => {
    if (customer) {
      setFormData({
        ...customer,
        move_date: customer.move_date ? customer.move_date.split('T')[0] : '',
        costs: customer.costs || { fuel: 0, labour: 0, parking: 0, tolls: 0, materials: 0, other: 0 }
      });
    } else {
      setFormData({
        name: '', phone: '', email: '', pickup_address: '', delivery_address: '',
        pickup_postcode: '', delivery_postcode: '', move_date: '', move_time: '',
        notes: '', lead_source: '', quote_amount: '', deposit_paid: '',
        remaining_balance: '', package_selected: '', status: 'new_enquiry',
        job_notes: '', costs: { fuel: 0, labour: 0, parking: 0, tolls: 0, materials: 0, other: 0 }
      });
    }
  }, [customer, isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name) {
      toast.error('Customer name is required');
      return;
    }
    onSave(formData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-zinc-900 border border-zinc-800 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-zinc-900 border-b border-zinc-800 p-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-white">
            {customer ? 'Edit Customer' : 'Add New Customer'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-zinc-800 rounded-lg">
            <XCircle className="w-5 h-5 text-zinc-500" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-zinc-500 mb-1">Full Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-amber-500 focus:outline-none"
                placeholder="John Smith"
              />
            </div>
            <div>
              <label className="block text-xs text-zinc-500 mb-1">Phone</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-amber-500 focus:outline-none"
                placeholder="07xxx xxxxxx"
              />
            </div>
            <div>
              <label className="block text-xs text-zinc-500 mb-1">Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-amber-500 focus:outline-none"
                placeholder="john@email.com"
              />
            </div>
            <div>
              <label className="block text-xs text-zinc-500 mb-1">Lead Source</label>
              <select
                value={formData.lead_source}
                onChange={(e) => setFormData({...formData, lead_source: e.target.value})}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-amber-500 focus:outline-none"
              >
                <option value="">Select source...</option>
                {LEAD_SOURCES.map(src => <option key={src} value={src}>{src}</option>)}
              </select>
            </div>
          </div>

          {/* Addresses */}
          <div className="border-t border-zinc-800 pt-4">
            <h3 className="text-sm font-semibold text-amber-500 mb-3">Move Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-zinc-500 mb-1">Pickup Address</label>
                <input
                  type="text"
                  value={formData.pickup_address}
                  onChange={(e) => setFormData({...formData, pickup_address: e.target.value})}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-amber-500 focus:outline-none"
                  placeholder="Full address"
                />
              </div>
              <div>
                <label className="block text-xs text-zinc-500 mb-1">Pickup Postcode</label>
                <input
                  type="text"
                  value={formData.pickup_postcode}
                  onChange={(e) => setFormData({...formData, pickup_postcode: e.target.value.toUpperCase()})}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-amber-500 focus:outline-none"
                  placeholder="SW1A 1AA"
                />
              </div>
              <div>
                <label className="block text-xs text-zinc-500 mb-1">Delivery Address</label>
                <input
                  type="text"
                  value={formData.delivery_address}
                  onChange={(e) => setFormData({...formData, delivery_address: e.target.value})}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-amber-500 focus:outline-none"
                  placeholder="Full address"
                />
              </div>
              <div>
                <label className="block text-xs text-zinc-500 mb-1">Delivery Postcode</label>
                <input
                  type="text"
                  value={formData.delivery_postcode}
                  onChange={(e) => setFormData({...formData, delivery_postcode: e.target.value.toUpperCase()})}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-amber-500 focus:outline-none"
                  placeholder="E1 6AN"
                />
              </div>
              <div>
                <label className="block text-xs text-zinc-500 mb-1">Move Date</label>
                <input
                  type="date"
                  value={formData.move_date}
                  onChange={(e) => setFormData({...formData, move_date: e.target.value})}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-amber-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-xs text-zinc-500 mb-1">Move Time</label>
                <input
                  type="time"
                  value={formData.move_time}
                  onChange={(e) => setFormData({...formData, move_time: e.target.value})}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-amber-500 focus:outline-none"
                />
              </div>
            </div>
          </div>

          {/* Quote & Payment */}
          <div className="border-t border-zinc-800 pt-4">
            <h3 className="text-sm font-semibold text-amber-500 mb-3">Quote & Payment</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs text-zinc-500 mb-1">Package</label>
                <select
                  value={formData.package_selected}
                  onChange={(e) => {
                    const pkg = PACKAGES.find(p => p.id === e.target.value);
                    setFormData({
                      ...formData, 
                      package_selected: e.target.value,
                      quote_amount: pkg ? pkg.price : formData.quote_amount
                    });
                  }}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-amber-500 focus:outline-none"
                >
                  <option value="">Select package...</option>
                  {PACKAGES.map(pkg => <option key={pkg.id} value={pkg.id}>{pkg.name} (£{pkg.price})</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-zinc-500 mb-1">Quote Amount (£)</label>
                <input
                  type="number"
                  value={formData.quote_amount}
                  onChange={(e) => setFormData({...formData, quote_amount: e.target.value})}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-amber-500 focus:outline-none"
                  placeholder="0"
                />
              </div>
              <div>
                <label className="block text-xs text-zinc-500 mb-1">Deposit Paid (£)</label>
                <input
                  type="number"
                  value={formData.deposit_paid}
                  onChange={(e) => {
                    const deposit = parseFloat(e.target.value) || 0;
                    const quote = parseFloat(formData.quote_amount) || 0;
                    setFormData({
                      ...formData, 
                      deposit_paid: e.target.value,
                      remaining_balance: (quote - deposit).toString()
                    });
                  }}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-amber-500 focus:outline-none"
                  placeholder="70"
                />
              </div>
            </div>
            <div className="mt-3">
              <label className="block text-xs text-zinc-500 mb-1">Status</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({...formData, status: e.target.value})}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-amber-500 focus:outline-none"
              >
                {Object.entries(CUSTOMER_STATUSES).map(([key, val]) => (
                  <option key={key} value={key}>{val.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Notes */}
          <div className="border-t border-zinc-800 pt-4">
            <label className="block text-xs text-zinc-500 mb-1">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-amber-500 focus:outline-none h-20 resize-none"
              placeholder="Any special requirements, items list, etc..."
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 bg-zinc-800 text-zinc-300 rounded-xl font-medium hover:bg-zinc-700 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-3 bg-gradient-to-r from-amber-500 to-amber-600 text-black rounded-xl font-bold hover:from-amber-400 hover:to-amber-500 transition-all"
            >
              {customer ? 'Update Customer' : 'Add Customer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// View Customer Modal
const ViewCustomerModal = ({ customer, isOpen, onClose, onEdit, onStatusChange, onOpenEmailBuilder, onSendEmail, onResend, onDelete }) => {
  if (!isOpen || !customer) return null;
  
  const status = CUSTOMER_STATUSES[customer.status] || CUSTOMER_STATUSES.new_enquiry;
  const StatusIcon = status.icon;
  const totalCosts = customer.costs ? Object.values(customer.costs).reduce((a, b) => a + (parseFloat(b) || 0), 0) : 0;
  const profit = (parseFloat(customer.quote_amount) || 0) - totalCosts;

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-zinc-900 border border-zinc-800 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-zinc-900 border-b border-zinc-800 p-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-white">{customer.name}</h2>
              <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium text-white ${status.color} mt-1`}>
                <StatusIcon className="w-3 h-3" />
                {status.label}
              </span>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-zinc-800 rounded-lg">
              <XCircle className="w-5 h-5 text-zinc-500" />
            </button>
          </div>
        </div>
        
        <div className="p-4 space-y-4">
          {/* Contact Info */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-zinc-800/50 rounded-lg p-3">
              <p className="text-xs text-zinc-500">Phone</p>
              <p className="text-white font-medium">{customer.phone || '-'}</p>
            </div>
            <div className="bg-zinc-800/50 rounded-lg p-3">
              <p className="text-xs text-zinc-500">Email</p>
              <p className="text-white font-medium truncate">{customer.email || '-'}</p>
            </div>
          </div>

          {/* Move Details */}
          <div className="bg-zinc-800/50 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-amber-500 mb-3">Move Details</h3>
            <div className="space-y-2">
              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-emerald-500 mt-0.5" />
                <div>
                  <p className="text-xs text-zinc-500">From</p>
                  <p className="text-white text-sm">{customer.pickup_address || customer.pickup_postcode || '-'}</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-red-500 mt-0.5" />
                <div>
                  <p className="text-xs text-zinc-500">To</p>
                  <p className="text-white text-sm">{customer.delivery_address || customer.delivery_postcode || '-'}</p>
                </div>
              </div>
              <div className="flex items-center gap-4 mt-3 pt-3 border-t border-zinc-700">
                <div>
                  <p className="text-xs text-zinc-500">Date</p>
                  <p className="text-white font-medium">{customer.move_date ? new Date(customer.move_date).toLocaleDateString('en-GB') : '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-zinc-500">Time</p>
                  <p className="text-white font-medium">{customer.move_time || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-zinc-500">Package</p>
                  <p className="text-white font-medium">{getPackageName(customer.package_selected)}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Financial */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-center">
              <p className="text-xs text-amber-500">Quote</p>
              <p className="text-xl font-bold text-amber-500">£{customer.quote_amount || 0}</p>
            </div>
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3 text-center">
              <p className="text-xs text-emerald-500">Deposit Paid</p>
              <p className="text-xl font-bold text-emerald-500">£{customer.deposit_paid || 0}</p>
            </div>
            <div className="bg-zinc-800 rounded-lg p-3 text-center">
              <p className="text-xs text-zinc-500">Balance Due</p>
              <p className="text-xl font-bold text-white">£{customer.remaining_balance || (parseFloat(customer.quote_amount) || 0) - (parseFloat(customer.deposit_paid) || 0)}</p>
            </div>
          </div>

          {/* All Package Prices (if quote was sent) */}
          {customer.package_prices && (
            <div className="bg-zinc-800/50 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-amber-500 mb-3">Quote Packages Offered</h3>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                {[
                  { id: 'van_man', name: 'Van & Man' },
                  { id: 'basic', name: 'Basic Move' },
                  { id: 'comfort', name: 'Comfort' },
                  { id: 'complete_comfort', name: 'Complete Comfort' },
                  { id: 'premium', name: 'Premium' }
                ].map(pkg => {
                  const price = customer.package_prices[pkg.id];
                  // Check if this package is selected (by ID or name)
                  const selectedPkg = getPackageInfo(customer.package_selected);
                  const isSelected = selectedPkg?.id === pkg.id;
                  return (
                    <div 
                      key={pkg.id} 
                      className={`rounded-lg p-2 text-center ${
                        isSelected 
                          ? 'bg-amber-500/20 border border-amber-500' 
                          : 'bg-zinc-700/50'
                      }`}
                    >
                      <p className="text-[10px] text-zinc-400">{pkg.name}</p>
                      <p className={`text-sm font-bold ${isSelected ? 'text-amber-500' : 'text-white'}`}>
                        £{price || '-'}
                      </p>
                      {isSelected && (
                        <span className="text-[8px] text-amber-500 font-medium">SELECTED</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Profit (for completed jobs) */}
          {customer.status === 'completed' && (
            <div className={`rounded-lg p-4 text-center ${profit >= 0 ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
              <p className="text-xs text-zinc-400">Job Profit/Loss</p>
              <p className={`text-2xl font-bold ${profit >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                {profit >= 0 ? '+' : ''}£{profit.toFixed(2)}
              </p>
              <p className="text-xs text-zinc-500 mt-1">Revenue £{customer.quote_amount || 0} - Costs £{totalCosts.toFixed(2)}</p>
            </div>
          )}

          {/* Lead Source & Notes */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-zinc-800/50 rounded-lg p-3">
              <p className="text-xs text-zinc-500">Lead Source</p>
              <p className="text-white font-medium">{customer.lead_source || '-'}</p>
            </div>
            <div className="bg-zinc-800/50 rounded-lg p-3">
              <p className="text-xs text-zinc-500">Created</p>
              <p className="text-white font-medium">{customer.created_at ? new Date(customer.created_at).toLocaleDateString('en-GB') : '-'}</p>
            </div>
          </div>

          {customer.notes && (
            <div className="bg-zinc-800/50 rounded-lg p-3">
              <p className="text-xs text-zinc-500 mb-1">Notes</p>
              <p className="text-white text-sm">{customer.notes}</p>
            </div>
          )}

          {/* Email History */}
          {customer.email_log?.length > 0 && (
            <div className="bg-zinc-800/50 rounded-lg p-4" data-testid="email-history-section">
              <h3 className="text-sm font-semibold text-amber-500 mb-3 flex items-center gap-2">
                <Mail className="w-4 h-4" /> Email History ({customer.email_log.length})
              </h3>
              <div className="space-y-2 max-h-52 overflow-y-auto pr-1">
                {[...customer.email_log]
                  .sort((a, b) => new Date(b.sent_at) - new Date(a.sent_at))
                  .map((e, i) => (
                    <div key={e.id || i} className="flex items-start justify-between gap-3 bg-zinc-900/70 border border-zinc-800 rounded-lg px-3 py-2" data-testid="email-history-entry">
                      <div className="min-w-0">
                        <p className="text-white text-xs font-semibold">
                          {EMAIL_LOG_LABELS[e.email_type] || e.email_type}
                          {e.resent_from && <span className="ml-1.5 text-[9px] text-cyan-400 font-medium">RESENT</span>}
                        </p>
                        {e.subject && <p className="text-zinc-500 text-[11px] truncate max-w-[280px]">{e.subject}</p>}
                        {e.sender_email && <p className="text-zinc-600 text-[10px]">from {e.sender_email}</p>}
                      </div>
                      <div className="text-right shrink-0 flex items-start gap-2">
                        <div>
                          <p className="text-zinc-400 text-[11px]">
                            {e.sent_at ? `${new Date(e.sent_at).toLocaleDateString('en-GB')} ${new Date(e.sent_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}` : '-'}
                          </p>
                          <span className={`inline-block mt-1 text-[9px] px-1.5 py-0.5 rounded-full font-medium ${e.source === 'email_builder' ? 'bg-purple-500/15 text-purple-400 border border-purple-500/30' : e.source === 'automation' ? 'bg-cyan-500/15 text-cyan-400 border border-cyan-500/30' : 'bg-amber-500/15 text-amber-400 border border-amber-500/30'}`}>
                            {e.source === 'email_builder' ? 'Email Builder' : e.source === 'automation' ? 'Automation' : 'Pro Manager'}
                          </span>
                        </div>
                        <button
                          onClick={() => onResend(customer, e)}
                          data-testid="email-history-resend-button"
                          title="Resend this email"
                          className="p-1.5 mt-0.5 bg-zinc-800 hover:bg-amber-500 hover:text-black text-amber-500 rounded-lg border border-zinc-700 transition-colors"
                        >
                          <RefreshCw className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Quick Status Change */}
          <div className="border-t border-zinc-800 pt-4">
            <p className="text-xs text-zinc-500 mb-2">Quick Status Update</p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(CUSTOMER_STATUSES).map(([key, val]) => (
                <button
                  key={key}
                  onClick={() => onStatusChange(customer, key)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    customer.status === key 
                      ? `${val.color} text-white` 
                      : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                  }`}
                >
                  {val.label}
                </button>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              onClick={() => onEdit(customer)}
              className="flex-1 px-4 py-3 bg-zinc-800 text-white rounded-xl font-medium hover:bg-zinc-700 transition-colors flex items-center justify-center gap-2"
            >
              <Edit className="w-4 h-4" /> Edit
            </button>
            <button
              onClick={() => onSendEmail(customer)}
              data-testid="modal-send-email-button"
              className="flex-1 px-4 py-3 bg-gradient-to-r from-amber-500 to-amber-600 text-black rounded-xl font-bold hover:from-amber-400 hover:to-amber-500 transition-all flex items-center justify-center gap-2"
            >
              <Send className="w-4 h-4" /> Send Email
            </button>
            <button
              onClick={() => onOpenEmailBuilder(customer)}
              data-testid="modal-open-email-builder-button"
              title="Open in Email Builder (full quote emails)"
              className="px-4 py-3 bg-zinc-800 text-amber-500 border border-zinc-700 rounded-xl font-medium hover:bg-zinc-700 transition-colors flex items-center justify-center gap-2"
            >
              <FileText className="w-4 h-4" />
            </button>
            <button
              onClick={() => onDelete(customer)}
              data-testid="modal-delete-customer-button"
              className="px-4 py-3 bg-red-950 text-red-400 border border-red-800 rounded-xl font-medium hover:bg-red-900 hover:text-red-300 transition-colors flex items-center justify-center gap-2"
              title="Delete customer"
            >
              <Trash2 className="w-4 h-4" /> Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Pro Manager Component
const ProManager = () => {
  const { user: authUser, logout } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [stats, setStats] = useState(null);
  const [reminders, setReminders] = useState([]);
  const [autoChase, setAutoChase] = useState({ queue: [], recently_chased: [] });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [branchFilter, setBranchFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [viewingCustomer, setViewingCustomer] = useState(null);
  const [emailModal, setEmailModal] = useState(null); // { customer, templateKey }

  const isHeadOffice = authUser?.role === 'head_office';

  // Fetch customers
  const fetchCustomers = useCallback(async () => {
    try {
      const params = isHeadOffice && branchFilter !== 'all' ? `?branch=${branchFilter}` : '';
      const response = await axios.get(`${API}/api/pro-manager/customers${params}`);
      setCustomers(response.data.customers || []);
    } catch (error) {
      console.error('Error fetching customers:', error);
      toast.error('Failed to load customers');
    }
  }, [isHeadOffice, branchFilter]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const params = isHeadOffice && branchFilter !== 'all' ? `?branch=${branchFilter}` : '';
      const response = await axios.get(`${API}/api/pro-manager/stats${params}`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, [isHeadOffice, branchFilter]);

  // Fetch reminders (customers to chase)
  const fetchReminders = useCallback(async () => {
    try {
      const params = isHeadOffice && branchFilter !== 'all' ? `?branch=${branchFilter}` : '';
      const response = await axios.get(`${API}/api/pro-manager/reminders${params}`);
      setReminders(response.data.reminders || []);
    } catch (error) {
      console.error('Error fetching reminders:', error);
    }
  }, [isHeadOffice, branchFilter]);

  // Fetch auto-chase queue (automatic quote follow-ups)
  const fetchAutoChase = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/pro-manager/auto-chase`);
      setAutoChase(response.data || { queue: [], recently_chased: [] });
    } catch (error) {
      console.error('Error fetching auto-chase:', error);
    }
  }, []);

  // Trigger a chase email immediately
  const handleChaseNow = async (item) => {
    if (!window.confirm(`Send the follow-up chase email to ${item.name} (${item.email}) now?`)) return;
    try {
      const { data } = await axios.post(`${API}/api/pro-manager/auto-chase/${item.id}/send-now`);
      toast.success(`Follow-up sent to ${item.name} from ${data.sender}`);
      fetchAutoChase();
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send follow-up');
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchCustomers(), fetchStats(), fetchReminders(), fetchAutoChase()]);
      setLoading(false);
    };
    loadData();
  }, [fetchCustomers, fetchStats, fetchReminders, fetchAutoChase]);

  // Branch permission check: branch users need to confirm for customers with sent quotes
  const checkEditPermission = (customer, action) => {
    if (isHeadOffice) return true;
    if (customer.quote_sent_at || customer.confirm_sent_at || customer.deposit_email_sent_at) {
      return window.confirm(
        `⚠️ PERMISSION REQUIRED\n\nA quote has already been sent to "${customer.name}".\n\nAre you sure you want to ${action} this customer? This action is tracked and Head Office will be able to see the change.\n\nClick OK to proceed or Cancel to abort.`
      );
    }
    return true;
  };

  // Save customer
  const handleSaveCustomer = async (customerData) => {
    try {
      if (editingCustomer) {
        await axios.put(`${API}/api/pro-manager/customers/${editingCustomer.id}`, customerData);
        toast.success('Customer updated!');
      } else {
        await axios.post(`${API}/api/pro-manager/customers`, customerData);
        toast.success('Customer added!');
      }
      setShowAddModal(false);
      setEditingCustomer(null);
      fetchCustomers();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save customer');
    }
  };

  // Delete customer
  const handleDeleteCustomer = async (customer) => {
    if (!checkEditPermission(customer, 'DELETE')) return;
    if (!window.confirm(`Delete ${customer.name}?`)) return;
    try {
      await axios.delete(`${API}/api/pro-manager/customers/${customer.id}`);
      toast.success('Customer deleted');
      setViewingCustomer(null);
      fetchCustomers();
      fetchStats();
      fetchReminders();
    } catch (error) {
      toast.error('Failed to delete customer');
    }
  };

  // Update status
  const handleStatusChange = async (customer, newStatus) => {
    try {
      await axios.patch(`${API}/api/pro-manager/customers/${customer.id}/status`, { status: newStatus });
      toast.success(`Status updated to ${CUSTOMER_STATUSES[newStatus].label}`);
      fetchCustomers();
      fetchStats();
      fetchReminders();
      if (viewingCustomer?.id === customer.id) {
        setViewingCustomer({...customer, status: newStatus});
      }
      // Offer direct email send for key stages (follow up, deposit request, booked, completed)
      const templateKey = STATUS_EMAIL_MAP[newStatus];
      if (templateKey) {
        if (customer.email) {
          setEmailModal({ customer: { ...customer, status: newStatus }, templateKey });
        } else {
          toast.info(`${customer.name} has no email address on file — add one to send emails directly`);
        }
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  // Open Email Builder with customer data
  const handleOpenEmailBuilder = (customer) => {
    // Store customer in localStorage for Email Builder to pick up
    localStorage.setItem('promanager_selected_customer', JSON.stringify(customer));
    window.open('/', '_blank');
  };

  // One-click resend of an email from the history
  const handleResendEmail = async (customer, entry) => {
    const label = EMAIL_LOG_LABELS[entry.email_type] || entry.email_type;
    const composeFallback = () => {
      toast.info('Original content not stored — review the pre-filled email and hit Send Now');
      const pmTemplates = ['follow_up', 'deposit_request', 'booking_confirmed', 'review_request'];
      const templateKey = pmTemplates.includes(entry.email_type) ? entry.email_type : (STATUS_EMAIL_MAP[customer.status] || 'follow_up');
      setEmailModal({ customer, templateKey });
    };
    if (!entry.id) { composeFallback(); return; }
    if (!window.confirm(`Resend "${label}" email to ${entry.to_email || customer.email}?`)) return;
    try {
      const { data } = await axios.post(`${API}/api/pro-manager/customers/${customer.id}/email-log/${entry.id}/resend`);
      toast.success(`Email resent to ${data.to_email}`);
      setViewingCustomer((v) => (v && v.id === customer.id ? { ...v, email_log: [...(v.email_log || []), data.entry] } : v));
      fetchCustomers();
    } catch (error) {
      if (error.response?.status === 409) {
        composeFallback();
      } else {
        toast.error(error.response?.data?.detail || 'Failed to resend email');
      }
    }
  };

  // Edit with permission check
  const handleEditCustomer = (c) => {
    if (!checkEditPermission(c, 'EDIT')) return;
    setEditingCustomer(c);
    setShowAddModal(true);
  };

  // Filter customers
  const filteredCustomers = customers.filter(c => {
    const matchesSearch = !searchQuery || 
      c.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.phone?.includes(searchQuery) ||
      c.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.pickup_postcode?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.delivery_postcode?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || c.status === statusFilter;
    const matchesSource = sourceFilter === 'all' || c.lead_source === sourceFilter;
    return matchesSearch && matchesStatus && matchesSource;
  });

  // Group by status for Kanban view
  const customersByStatus = Object.keys(CUSTOMER_STATUSES).reduce((acc, status) => {
    acc[status] = filteredCustomers.filter(c => c.status === status);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-amber-500 animate-spin mx-auto mb-4" />
          <p className="text-zinc-500">Loading Pro Manager...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-black/95 backdrop-blur border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl flex items-center justify-center">
                <Building2 className="w-6 h-6 text-black" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-white">HomeGo Pro Manager</h1>
                <p className="text-xs text-zinc-500">{authUser?.branch_name ? `${authUser.branch_name} Branch` : 'Business Management System'}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {isHeadOffice && (
                <select
                  value={branchFilter}
                  onChange={(e) => setBranchFilter(e.target.value)}
                  data-testid="branch-filter-select"
                  className="px-3 py-2 bg-zinc-800 text-white rounded-xl text-sm border border-zinc-700 focus:border-amber-500 outline-none"
                >
                  {BRANCH_LIST.map((b) => (
                    <option key={b.value} value={b.value}>{b.label}</option>
                  ))}
                </select>
              )}
              <span className="hidden md:flex px-3 py-2 bg-amber-500 text-black rounded-xl text-xs font-bold items-center" data-testid="pm-branch-badge">
                {authUser?.branch_name || ''}
              </span>
              <a
                href="/"
                target="_blank"
                rel="noopener noreferrer"
                className="px-3 py-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-xl text-sm font-medium flex items-center gap-2 border border-zinc-700"
              >
                <Mail className="w-4 h-4 text-amber-500" />
                <span className="hidden sm:inline">Email Builder</span>
              </a>
              <button
                onClick={() => { setEditingCustomer(null); setShowAddModal(true); }}
                className="px-4 py-2 bg-gradient-to-r from-amber-500 to-amber-600 text-black rounded-xl font-bold text-sm hover:from-amber-400 hover:to-amber-500 transition-all flex items-center gap-2"
              >
                <Plus className="w-4 h-4" /> Add Customer
              </button>
              <button
                onClick={logout}
                data-testid="pm-logout-button"
                className="px-3 py-2 bg-zinc-800 hover:bg-red-900 text-white rounded-xl text-sm font-medium flex items-center gap-2 border border-zinc-700"
                title="Sign out"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <nav className="sticky top-[65px] z-30 bg-black border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex gap-1 overflow-x-auto py-2 scrollbar-hide">
            {[
              { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
              { id: 'reminders', label: 'Reminders', icon: Bell, badge: reminders.length },
              { id: 'customers', label: 'All Customers', icon: Users },
              { id: 'pipeline', label: 'Pipeline', icon: TrendingUp },
              { id: 'calendar', label: 'Calendar', icon: Calendar },
              { id: 'reports', label: 'Reports', icon: FileText },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`pm-tab-${tab.id}`}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                  activeTab === tab.id
                    ? 'bg-amber-500/10 text-amber-500'
                    : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
                {tab.badge > 0 && (
                  <span className="px-1.5 py-0.5 bg-amber-500 text-black rounded-full text-[10px] font-bold leading-none">{tab.badge}</span>
                )}
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <div className="space-y-6">
            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
              <StatCard title="New Enquiries Today" value={stats?.enquiries_today || 0} icon={Users} />
              <StatCard title="Quotes Sent Today" value={stats?.quotes_today || 0} icon={FileText} />
              <StatCard title="Pending Quotes" value={stats?.pending_quotes || 0} icon={Clock} />
              <StatCard title="Deposits This Week" value={stats?.deposits_this_week || 0} icon={CreditCard} />
              <StatCard title="Jobs Booked" value={stats?.jobs_booked || 0} icon={Calendar} />
              <StatCard title="Completed This Month" value={stats?.completed_this_month || 0} icon={CheckCircle} />
              <StatCard title="Monthly Revenue" value={`£${stats?.monthly_revenue?.toLocaleString() || 0}`} icon={DollarSign} />
              <StatCard title="Monthly Profit" value={`£${stats?.monthly_profit?.toLocaleString() || 0}`} icon={TrendingUp} trend={stats?.profit_trend} trendUp={stats?.profit_trend_up} />
              <StatCard title="Conversion Rate" value={`${stats?.conversion_rate || 0}%`} icon={ArrowUpRight} />
              <StatCard title="Lost Jobs" value={stats?.lost_jobs || 0} icon={XCircle} color="red" />
            </div>

            {/* Chase Today - Reminders */}
            {reminders.length > 0 && (
              <div className="bg-zinc-900 border-2 border-amber-500/50 rounded-xl p-4" data-testid="chase-today-panel">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-bold text-white flex items-center gap-2">
                    <Bell className="w-5 h-5 text-amber-500 animate-pulse" />
                    Chase Today <span className="px-2 py-0.5 bg-amber-500 text-black rounded-full text-xs font-bold">{reminders.length}</span>
                  </h2>
                  <p className="text-xs text-zinc-500">Quotes sent 1+ days ago — follow up with these customers</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {reminders.slice(0, 6).map((r) => (
                    <div key={r.id} className="bg-zinc-800 border border-amber-500/20 rounded-xl p-3 cursor-pointer hover:border-amber-500/60 transition-all" onClick={() => setViewingCustomer(r)} data-testid={`reminder-card-${r.id}`}>
                      <div className="flex items-center justify-between mb-1">
                        <h3 className="font-semibold text-white text-sm truncate">{r.name}</h3>
                        <span className="px-2 py-0.5 bg-red-500/20 text-red-400 rounded-full text-[10px] font-bold whitespace-nowrap">
                          {r.days_waiting}d waiting
                        </span>
                      </div>
                      <p className="text-xs text-zinc-400 truncate">{r.phone || r.email || 'No contact'}</p>
                      {formatAppointment(r) && (
                        <p className="text-xs text-[#D4AF37] font-bold mt-1">
                          {r.status === 'reminder_callback' ? '📞 Call' : '🏠 Viewing'} {r.name?.split(' ')[0]} — {formatAppointment(r)}
                        </p>
                      )}
                      <div className="flex items-center gap-2 mt-2">
                        {r.phone && (
                          <a href={`tel:${r.phone}`} onClick={(e) => e.stopPropagation()} className="flex-1 py-1.5 bg-amber-500 text-black rounded-lg text-xs font-bold text-center">
                            📞 Call Now
                          </a>
                        )}
                        <select
                          value={r.status}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => handleStatusChange(r, e.target.value)}
                          className="flex-1 py-1.5 px-2 bg-zinc-700 text-white rounded-lg text-xs border border-zinc-600 outline-none"
                        >
                          {Object.entries(CUSTOMER_STATUSES).map(([key, val]) => (
                            <option key={key} value={key}>{val.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Quick Status Overview */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
              <h2 className="text-lg font-bold text-white mb-4">Pipeline Overview</h2>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {Object.entries(CUSTOMER_STATUSES).slice(0, 5).map(([key, val]) => {
                  const count = customersByStatus[key]?.length || 0;
                  return (
                    <button
                      key={key}
                      onClick={() => { setStatusFilter(key); setActiveTab('customers'); }}
                      className="bg-zinc-800 rounded-xl p-4 text-center hover:bg-zinc-700 transition-all"
                    >
                      <div className={`w-10 h-10 ${val.color} rounded-full flex items-center justify-center mx-auto mb-2`}>
                        <val.icon className="w-5 h-5 text-white" />
                      </div>
                      <p className="text-2xl font-bold text-white">{count}</p>
                      <p className="text-xs text-zinc-500">{val.label}</p>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Recent Customers */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-white">Recent Customers</h2>
                <button 
                  onClick={() => setActiveTab('customers')}
                  className="text-amber-500 text-sm hover:underline"
                >
                  View All
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {customers.slice(0, 6).map(customer => (
                  <CustomerRow
                    key={customer.id}
                    customer={customer}
                    onView={setViewingCustomer}
                    onEdit={handleEditCustomer}
                    onStatusChange={handleStatusChange}
                    onDelete={handleDeleteCustomer}
                  />
                ))}
              </div>
              {customers.length === 0 && (
                <div className="text-center py-12">
                  <Users className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
                  <p className="text-zinc-500">No customers yet</p>
                  <button
                    onClick={() => setShowAddModal(true)}
                    className="mt-4 px-4 py-2 bg-amber-500 text-black rounded-lg font-medium"
                  >
                    Add First Customer
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Reminders Tab */}
        {activeTab === 'reminders' && (
          <div className="space-y-6" data-testid="reminders-tab-content">
            {/* Auto Chase - automatic quote follow-ups */}
            <div className="bg-zinc-900 border-2 border-cyan-500/50 rounded-xl p-4" data-testid="auto-chase-section">
              <div className="flex items-center justify-between mb-1">
                <h2 className="text-lg font-bold text-white">🤖 Auto Chase <span className="ml-1 px-2 py-0.5 bg-cyan-500/20 text-cyan-400 rounded-full text-xs font-bold">{autoChase.queue?.length || 0}</span></h2>
              </div>
              <p className="text-xs text-zinc-500 mb-4">Quotes chase themselves — a friendly follow-up is sent automatically after {autoChase.followup_after_days || 3} days of silence. One chase per customer, logged in their Email History.</p>
              {(!autoChase.queue || autoChase.queue.length === 0) ? (
                <p className="text-sm text-zinc-600 py-4 text-center">No quotes waiting — the robot's got nothing to chase! ✅</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {autoChase.queue.map((item) => (
                    <div key={item.id} className="bg-zinc-800 border border-zinc-700 rounded-xl p-3" data-testid={`auto-chase-card-${item.id}`}>
                      <div className="flex items-center justify-between mb-1">
                        <h3 className="font-semibold text-white text-sm truncate">{item.name}</h3>
                        {item.is_due ? (
                          <span className="px-2 py-0.5 bg-cyan-500/20 text-cyan-400 rounded-full text-[10px] font-bold whitespace-nowrap">Sending soon</span>
                        ) : (
                          <span className="px-2 py-0.5 bg-zinc-700 text-zinc-300 rounded-full text-[10px] font-bold whitespace-nowrap">Chases in {item.days_until_due}d</span>
                        )}
                      </div>
                      <p className="text-xs text-zinc-400 truncate">{item.email}</p>
                      {item.quote_amount && <p className="text-xs text-amber-400 font-bold mt-0.5">£{item.quote_amount}</p>}
                      {isHeadOffice && item.branch && (
                        <span className="inline-block mt-1 px-2 py-0.5 bg-zinc-700 text-amber-400 rounded-full text-[10px] font-medium">{BRANCH_LABELS[item.branch] || item.branch}</span>
                      )}
                      <button
                        onClick={() => handleChaseNow(item)}
                        className="w-full mt-2 py-1.5 bg-cyan-600 hover:bg-cyan-500 text-white rounded-lg text-xs font-bold transition-colors"
                        data-testid={`chase-now-button-${item.id}`}
                      >
                        ⚡ Chase Now
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {autoChase.results?.total_chased > 0 && (
                <div className="mt-4 pt-4 border-t border-zinc-800" data-testid="chase-results-section">
                  <p className="text-[11px] text-zinc-500 mb-3 font-semibold uppercase tracking-wide">📈 Chase Results — is the robot earning its keep?</p>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3">
                    <div className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-center">
                      <p className="text-lg font-bold text-white" data-testid="chase-stat-total">{autoChase.results.total_chased}</p>
                      <p className="text-[10px] text-zinc-500 uppercase">Chased</p>
                    </div>
                    <div className="bg-emerald-950/50 border border-emerald-700/50 rounded-lg px-3 py-2 text-center">
                      <p className="text-lg font-bold text-emerald-400" data-testid="chase-stat-booked">{autoChase.results.booked}</p>
                      <p className="text-[10px] text-emerald-600 uppercase">Booked</p>
                    </div>
                    <div className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-center">
                      <p className="text-lg font-bold text-amber-400" data-testid="chase-stat-waiting">{autoChase.results.waiting}</p>
                      <p className="text-[10px] text-zinc-500 uppercase">Waiting</p>
                    </div>
                    <div className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-center">
                      <p className="text-lg font-bold text-red-400" data-testid="chase-stat-lost">{autoChase.results.lost}</p>
                      <p className="text-[10px] text-zinc-500 uppercase">Lost</p>
                    </div>
                    <div className="bg-cyan-950/50 border border-cyan-700/50 rounded-lg px-3 py-2 text-center">
                      <p className="text-lg font-bold text-cyan-400" data-testid="chase-stat-conversion">{autoChase.results.conversion_rate}%</p>
                      <p className="text-[10px] text-cyan-600 uppercase">Win Rate{autoChase.results.revenue_won > 0 ? ` · £${autoChase.results.revenue_won}` : ''}</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {autoChase.results.list.map((c) => (
                      <span
                        key={c.id}
                        className={`px-2 py-1 rounded-lg text-[11px] border cursor-default ${
                          c.outcome === 'booked' ? 'bg-emerald-950/60 border-emerald-700/60 text-emerald-300'
                          : c.outcome === 'lost' ? 'bg-red-950/50 border-red-800/50 text-red-300'
                          : c.outcome === 'progressing' ? 'bg-amber-950/50 border-amber-700/50 text-amber-300'
                          : 'bg-zinc-800 border-zinc-700 text-zinc-300'
                        }`}
                        data-testid="chase-result-chip"
                        title={`Chased ${c.auto_followup_sent_at ? new Date(c.auto_followup_sent_at).toLocaleDateString('en-GB') : ''} — ${CUSTOMER_STATUSES[c.status]?.label || c.status}`}
                      >
                        {c.outcome === 'booked' ? '✅' : c.outcome === 'lost' ? '❌' : c.outcome === 'progressing' ? '🔥' : '⏳'} {c.name}{c.outcome === 'booked' && c.quote_amount ? ` · £${c.quote_amount}` : ''}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
            {[
              { key: 'reminder_callback', title: '📞 Reminder / Callback', desc: 'Customers waiting for a callback', border: 'border-amber-500/50' },
              { key: 'property_view', title: '🏠 Property View', desc: 'Property viewings to arrange or attend', border: 'border-teal-500/50' },
              { key: 'overdue', title: '⏰ Overdue Quotes', desc: 'Quotes sent 1+ days ago with no response — chase these!', border: 'border-red-500/50' },
            ].map(section => {
              const items = section.key === 'overdue'
                ? reminders.filter(r => !['reminder_callback', 'property_view'].includes(r.status))
                : reminders.filter(r => r.status === section.key);
              return (
                <div key={section.key} className={`bg-zinc-900 border-2 ${section.border} rounded-xl p-4`} data-testid={`reminders-section-${section.key}`}>
                  <div className="flex items-center justify-between mb-1">
                    <h2 className="text-lg font-bold text-white">{section.title} <span className="ml-1 px-2 py-0.5 bg-zinc-700 text-amber-400 rounded-full text-xs font-bold">{items.length}</span></h2>
                  </div>
                  <p className="text-xs text-zinc-500 mb-4">{section.desc}</p>
                  {items.length === 0 ? (
                    <p className="text-sm text-zinc-600 py-4 text-center">Nothing here — all caught up! ✅</p>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {items.map((r) => (
                        <div key={r.id} className="bg-zinc-800 border border-zinc-700 rounded-xl p-3 cursor-pointer hover:border-amber-500/60 transition-all" onClick={() => setViewingCustomer(r)}>
                          <div className="flex items-center justify-between mb-1">
                            <h3 className="font-semibold text-white text-sm truncate">{r.name}</h3>
                            <span className="px-2 py-0.5 bg-red-500/20 text-red-400 rounded-full text-[10px] font-bold whitespace-nowrap">
                              {r.days_waiting}d waiting
                            </span>
                          </div>
                          <p className="text-xs text-zinc-400 truncate">{r.phone || r.email || 'No contact'}</p>
                          {formatAppointment(r) && (
                            <p className="text-xs text-[#D4AF37] font-bold mt-1" data-testid={`reminder-appointment-${r.id}`}>
                              {r.status === 'reminder_callback' ? '📞 Call' : '🏠 Viewing'} {r.name?.split(' ')[0]} — {formatAppointment(r)}
                            </p>
                          )}
                          {isHeadOffice && r.branch && (
                            <span className="inline-block mt-1 px-2 py-0.5 bg-zinc-700 text-amber-400 rounded-full text-[10px] font-medium">{BRANCH_LABELS[r.branch] || r.branch}</span>
                          )}
                          <div className="flex items-center gap-2 mt-2">
                            {r.phone && (
                              <a href={`tel:${r.phone}`} onClick={(e) => e.stopPropagation()} className="flex-1 py-1.5 bg-amber-500 text-black rounded-lg text-xs font-bold text-center">
                                📞 Call Now
                              </a>
                            )}
                            <select
                              value={r.status}
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) => handleStatusChange(r, e.target.value)}
                              className="flex-1 py-1.5 px-2 bg-zinc-700 text-white rounded-lg text-xs border border-zinc-600 outline-none"
                            >
                              {Object.entries(CUSTOMER_STATUSES).map(([key, val]) => (
                                <option key={key} value={key}>{val.label}</option>
                              ))}
                            </select>
                            <button
                              onClick={(e) => { e.stopPropagation(); handleDeleteCustomer(r); }}
                              className="p-1.5 bg-zinc-700 text-red-400 rounded-lg hover:bg-red-900 hover:text-red-300 border border-zinc-600"
                              title="Delete customer"
                              data-testid={`reminder-delete-${r.id}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Customers Tab */}
        {activeTab === 'customers' && (
          <div className="space-y-4">
            {/* Search & Filters */}
            <div className="flex flex-col md:flex-row gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                <input
                  type="text"
                  placeholder="Search by name, phone, email, postcode..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full bg-zinc-900 border border-zinc-800 rounded-xl pl-10 pr-4 py-3 text-white focus:border-amber-500 focus:outline-none"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white focus:border-amber-500 focus:outline-none"
              >
                <option value="all">All Statuses</option>
                {Object.entries(CUSTOMER_STATUSES).map(([key, val]) => (
                  <option key={key} value={key}>{val.label}</option>
                ))}
              </select>
              <select
                value={sourceFilter}
                onChange={(e) => setSourceFilter(e.target.value)}
                className="bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white focus:border-amber-500 focus:outline-none"
              >
                <option value="all">All Sources</option>
                {LEAD_SOURCES.map(src => (
                  <option key={src} value={src}>{src}</option>
                ))}
              </select>
            </div>

            {/* Customer Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredCustomers.map(customer => (
                <CustomerRow
                  key={customer.id}
                  customer={customer}
                  onView={setViewingCustomer}
                  onEdit={(c) => { setEditingCustomer(c); setShowAddModal(true); }}
                  onStatusChange={handleStatusChange}
                  onDelete={handleDeleteCustomer}
                />
              ))}
            </div>

            {filteredCustomers.length === 0 && (
              <div className="text-center py-12">
                <Search className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
                <p className="text-zinc-500">No customers found</p>
              </div>
            )}
          </div>
        )}

        {/* Pipeline Tab */}
        {activeTab === 'pipeline' && (
          <div className="overflow-x-auto pb-4">
            <div className="flex gap-4 min-w-max">
              {Object.entries(CUSTOMER_STATUSES).map(([key, val]) => (
                <div key={key} className="w-72 flex-shrink-0">
                  <div className={`${val.color} rounded-t-xl px-4 py-2 flex items-center justify-between`}>
                    <span className="font-semibold text-white text-sm">{val.label}</span>
                    <span className="bg-white/20 px-2 py-0.5 rounded-full text-xs text-white">
                      {customersByStatus[key]?.length || 0}
                    </span>
                  </div>
                  <div className="bg-zinc-900 border border-zinc-800 border-t-0 rounded-b-xl p-3 space-y-3 min-h-[200px]">
                    {customersByStatus[key]?.map(customer => (
                      <div
                        key={customer.id}
                        onClick={() => setViewingCustomer(customer)}
                        className="bg-zinc-800 rounded-lg p-3 cursor-pointer hover:bg-zinc-700 transition-all"
                      >
                        <p className="font-medium text-white text-sm truncate">{customer.name}</p>
                        <p className="text-xs text-zinc-500 mt-1">
                          {customer.pickup_postcode} → {customer.delivery_postcode}
                        </p>
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-amber-500 font-bold text-sm">£{customer.quote_amount || 0}</span>
                          <span className="text-[10px] text-zinc-500">{customer.lead_source}</span>
                        </div>
                      </div>
                    ))}
                    {(!customersByStatus[key] || customersByStatus[key].length === 0) && (
                      <p className="text-center text-zinc-600 text-xs py-4">No customers</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Calendar Tab */}
        {activeTab === 'calendar' && (
          <div className="space-y-6">
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
              <h2 className="text-lg font-bold text-white mb-4">
                Upcoming Jobs
                <span className="ml-2 px-2 py-0.5 bg-amber-500 text-black rounded-full text-xs font-bold" data-testid="upcoming-jobs-count">
                  {customers.filter(c => c.move_date && ['job_booked', 'deposit_paid', 'in_progress'].includes(c.status)).length}
                </span>
              </h2>
              {customers
                .filter(c => c.move_date && ['job_booked', 'deposit_paid', 'in_progress'].includes(c.status))
                .sort((a, b) => new Date(a.move_date) - new Date(b.move_date))
                .map(customer => {
                  const moveDate = new Date(customer.move_date);
                  const isToday = moveDate.toDateString() === new Date().toDateString();
                  const isTomorrow = moveDate.toDateString() === new Date(Date.now() + 86400000).toDateString();
                  return (
                    <div
                      key={customer.id}
                      onClick={() => setViewingCustomer(customer)}
                      className={`flex items-center gap-4 p-4 rounded-xl mb-3 cursor-pointer transition-all ${
                        isToday ? 'bg-amber-500/10 border border-amber-500/30' : 
                        isTomorrow ? 'bg-blue-500/10 border border-blue-500/30' : 
                        'bg-zinc-800 hover:bg-zinc-700'
                      }`}
                    >
                      <div className="text-center min-w-[60px]">
                        <p className={`text-2xl font-bold ${isToday ? 'text-amber-500' : isTomorrow ? 'text-blue-500' : 'text-white'}`}>
                          {moveDate.getDate()}
                        </p>
                        <p className="text-xs text-zinc-500">
                          {moveDate.toLocaleDateString('en-GB', { month: 'short' })}
                        </p>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-white">{customer.name}</p>
                          {isToday && <span className="bg-amber-500 text-black text-[10px] px-2 py-0.5 rounded-full font-bold">TODAY</span>}
                          {isTomorrow && <span className="bg-blue-500 text-white text-[10px] px-2 py-0.5 rounded-full font-bold">TOMORROW</span>}
                        </div>
                        <p className="text-sm text-zinc-400">{customer.pickup_postcode} → {customer.delivery_postcode}</p>
                        <p className="text-xs text-zinc-500 mt-1">{customer.move_time || 'Time TBC'} • {getPackageName(customer.package_selected)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-amber-500 font-bold">£{customer.quote_amount || 0}</p>
                        <p className="text-xs text-zinc-500">Balance: £{(parseFloat(customer.quote_amount) || 0) - (parseFloat(customer.deposit_paid) || 0)}</p>
                      </div>
                    </div>
                  );
                })}
              {customers.filter(c => c.move_date && ['job_booked', 'deposit_paid', 'in_progress'].includes(c.status)).length === 0 && (
                <div className="text-center py-12">
                  <Calendar className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
                  <p className="text-zinc-500">No upcoming jobs</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Reports Tab */}
        {activeTab === 'reports' && (
          <div className="space-y-6">
            {/* Lead Source Report */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
              <h2 className="text-lg font-bold text-white mb-4">Lead Source Performance</h2>
              <div className="space-y-3">
                {LEAD_SOURCES.map(source => {
                  const sourceCustomers = customers.filter(c => c.lead_source === source);
                  const bookings = sourceCustomers.filter(c => ['job_booked', 'in_progress', 'completed'].includes(c.status));
                  const revenue = bookings.reduce((sum, c) => sum + (parseFloat(c.quote_amount) || 0), 0);
                  const conversionRate = sourceCustomers.length > 0 ? ((bookings.length / sourceCustomers.length) * 100).toFixed(0) : 0;
                  
                  if (sourceCustomers.length === 0) return null;
                  
                  return (
                    <div key={source} className="flex items-center gap-4 p-3 bg-zinc-800 rounded-lg">
                      <div className="flex-1">
                        <p className="font-medium text-white">{source}</p>
                        <div className="flex items-center gap-4 mt-1 text-xs text-zinc-500">
                          <span>{sourceCustomers.length} leads</span>
                          <span>{bookings.length} bookings</span>
                          <span className="text-emerald-500">{conversionRate}% conversion</span>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-amber-500 font-bold">£{revenue.toLocaleString()}</p>
                        <p className="text-xs text-zinc-500">revenue</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Package Performance */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
              <h2 className="text-lg font-bold text-white mb-4">Package Performance</h2>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                {PACKAGES.map(pkg => {
                  const pkgCustomers = customers.filter(c => c.package_selected === pkg.id);
                  const completed = pkgCustomers.filter(c => c.status === 'completed');
                  const revenue = pkgCustomers.reduce((sum, c) => sum + (parseFloat(c.quote_amount) || 0), 0);
                  
                  return (
                    <div key={pkg.id} className="bg-zinc-800 rounded-xl p-4 text-center">
                      <p className="font-medium text-white text-sm">{pkg.name}</p>
                      <p className="text-2xl font-bold text-amber-500 mt-2">{pkgCustomers.length}</p>
                      <p className="text-xs text-zinc-500">bookings</p>
                      <p className="text-sm text-emerald-500 mt-2">£{revenue.toLocaleString()}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Modals */}
      <CustomerModal
        customer={editingCustomer}
        isOpen={showAddModal}
        onClose={() => { setShowAddModal(false); setEditingCustomer(null); }}
        onSave={handleSaveCustomer}
      />

      <ViewCustomerModal
        customer={viewingCustomer}
        isOpen={!!viewingCustomer}
        onClose={() => setViewingCustomer(null)}
        onEdit={(c) => { setEditingCustomer(c); setShowAddModal(true); setViewingCustomer(null); }}
        onStatusChange={handleStatusChange}
        onOpenEmailBuilder={handleOpenEmailBuilder}
        onSendEmail={(c) => setEmailModal({ customer: c, templateKey: STATUS_EMAIL_MAP[c.status] || 'follow_up' })}
        onResend={handleResendEmail}
        onDelete={handleDeleteCustomer}
      />

      {emailModal && (
        <SendEmailModal
          customer={emailModal.customer}
          initialTemplate={emailModal.templateKey}
          onClose={() => setEmailModal(null)}
          onSent={(logEntry) => {
            fetchCustomers(); fetchStats(); fetchReminders();
            if (viewingCustomer && viewingCustomer.id === emailModal.customer.id) {
              setViewingCustomer((v) => ({ ...v, email_log: [...(v.email_log || []), logEntry] }));
            }
          }}
        />
      )}
    </div>
  );
};

export default ProManager;
